import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../api';
import { AxiosResponse } from 'axios';
import {
  FetchClassroomsResponse,
  IClassRoomRequest,
  IClassRoomResponseBody,
  IDeleteClassroomRequest,
  IGetClassroomRequest,
} from '../../interface/classRoomModel';

const fetchClassRoom = async (page?: number, perPage?: number) => {
  const response = await api.get<IClassRoomResponseBody>(
    `/classroom?perPageRow=${perPage}&currentPage=${page}`,
  );
  return response.data;
};

export const useFetchClassRoom = (page?: number, perPage?: number) => {
  return useQuery(['classRoom', page, perPage], () => fetchClassRoom(page, perPage), {
    // Cached data will stay for 30 minutes
  });
};

const deleteClassRoom = async (classRoomId: string) => {
  const response = await api.delete(`/classroom/${classRoomId}`);
  return response.data;
};

/**
 * CVA-2933: Permanent classroom deletion with the gated cascade flow.
 * - Call with no payload (or { confirmed: false }) to trigger the gate checks.
 * - Call with { confirmed: true } after the admin confirms the impact modal.
 *
 * On a gate (HTTP 400) axios rejects, so the caller receives the structured
 * error body via `error.response.data` (handled in the redux thunk).
 * A DELETE body must be sent via the `data` config key.
 */
export const deleteClassroomFn = async (
  classroomId: string,
  payload?: IDeleteClassroomRequest,
): Promise<AxiosResponse> => {
  const response = await api.delete(`/classroom/${classroomId}`, {
    data: payload,
  });
  return response;
};

/**
 * Activate/inactivate a classroom via the dedicated status-toggle endpoint
 * (PATCH /v2/classroom/:id/status), driven by the table row
 * action. `status: true` activates (no gate). `status: false` inactivates: the
 * soft gate (active students) rejects with a structured 400 body (errorCode
 * CLASSROOM_INACTIVATE_HAS_STUDENTS + impact `data`) until re-called with
 * `confirmed: true`, which moves those students to the Unassigned Classroom.
 *
 * The shared axios instance rejects with the response BODY directly, so callers
 * receive the structured gate body in their catch block.
 */
export const setClassroomStatus = async (
  classroomId: string,
  status: boolean,
  confirmed?: boolean,
) => {
  const response = await api.patch(`/classroom/${classroomId}/status`, {
    status,
    confirmed,
  });
  return response.data;
};

export const myClassroom = async (
  params: IGetClassroomRequest,
): Promise<FetchClassroomsResponse> => {
  const response = await api.get<FetchClassroomsResponse>('/me', { params });

  return response.data;
};

export const useDeleteClassRoom = (subjectId: string) => {
  const queryClient = useQueryClient();
  return useMutation(() => deleteClassRoom(subjectId), {
    onSuccess: () => {
      queryClient.invalidateQueries('classRoom'); // Invalidate subjects query to refetch after deleting
    },
  });
};

const createClassRoom = async (classRoom: IClassRoomRequest) => {
  console.log(`cls`, classRoom);
  const response = await api.post(`/classroom`, classRoom);
  console.log(`class`, response.data);
  return response.data;
};

export const useCeateClassRoom = (classRoom: IClassRoomRequest) => {
  const queryClient = useQueryClient();
  return useMutation(() => createClassRoom(classRoom), {
    onSuccess: () => {
      queryClient.invalidateQueries('classRoom'); // Invalidate subjects query to refetch after deleting
    },
  });
};

const getReport = async () => {
  const response = await api
    .get(`/classroom/download`)
    .then((response: AxiosResponse<ArrayBuffer>) => handleDownload(response));
  //return response.data;
};

export const useGetReport = () => {
  const queryClient = useQueryClient();
  return useMutation(() => getReport(), {
    onSuccess: () => {
      queryClient.invalidateQueries('classRoom'); // Invalidate subjects query to refetch after deleting
    },
  });
};

const handleDownload = (response: AxiosResponse<ArrayBuffer>): void => {
  // Create an object URL from the response data (which is an ArrayBuffer)
  const fileUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })); // Create a link element
  const link = document.createElement('a'); // Set the link's href to the object URL created from the response data
  link.href = fileUrl; // Set the filename for the download
  link.download = 'report.pdf'; // Programmatically trigger a click on the link to start the download
  link.click();
  URL.revokeObjectURL(fileUrl);
};
